import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Upload,
  Space,
  Card,
  message,
} from 'antd';
import { UploadOutlined, UserOutlined, BankOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { scrollToFirstError } from '../../utils/formHelpers';
import ClientTaxonomyManager from './ClientTaxonomyManager';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { clientSubcategoriesApi } from '../../utils/api';
import PhoneDuplicateAlert from '../common/PhoneDuplicateAlert';
import { digitsOnlyPhone, usePhoneDuplicateCheck } from '../../hooks/usePhoneDuplicateCheck';

const { Option } = Select;
const { TextArea } = Input;

/** فقط ارقام؛ ارقام فارسی/عربی به لاتین تبدیل و بقیه حذف می‌شوند */
const normalizeMobileDigits = (value) => {
  if (value == null) return value;
  return digitsOnlyPhone(value);
};

export const buildClientFormData = (values, { imageFile, logoFile, deleteImage, deleteLogo, isEdit }) => {
  const fd = new FormData();
  const append = (k, v) => {
    if (v === undefined || v === null) return;
    fd.append(k, v);
  };

  // ویرایش مشتری: فقط فیلدهای مورد تایید API ارسال شود
  if (isEdit) {
    append('name', String(values.name ?? '').trim());
    append('mobile', values.mobile ?? '');
    append('gender', values.gender || 'male');
    append('category_id', values.category_id != null ? String(values.category_id) : '');
    append('sub_category_id', values.sub_category_id != null ? String(values.sub_category_id) : '');
    if (values.source_id != null && values.source_id !== '') {
      append('source_id', String(values.source_id));
    }
    append('login', values.login || 'disable');
    append('sendMail', values.sendMail || 'yes');
    append('status', values.status || 'active');
    append('company_name', values.company_name ?? '');
    append('website', values.website ?? '');
    append('office', values.office ?? '');
    append('city', values.city ?? '');
    append('state', values.state ?? '');
    append('postal_code', values.postal_code ?? '');
    append('address', values.address ?? '');
    append('shipping_address', values.shipping_address ?? '');
    if (values.added_by != null && values.added_by !== '') {
      append('added_by', String(values.added_by));
    }
    if (deleteImage) fd.append('image_delete', 'yes');
    if (deleteLogo) fd.append('company_logo_delete', 'yes');
    if (imageFile) fd.append('image', imageFile);
    if (logoFile) fd.append('company_logo', logoFile);
    return fd;
  }

  // ایجاد مشتری
  append('name', values.name ?? '');
  append('email', values.email ?? '');
  append('mobile', values.mobile ?? '');
  append('gender', values.gender || 'male');
  append('category_id', values.category_id != null ? String(values.category_id) : '');
  append('sub_category_id', values.sub_category_id != null ? String(values.sub_category_id) : '');
  if (values.source_id != null && values.source_id !== '') {
    append('source_id', String(values.source_id));
  }
  append('login', values.login || 'disable');
  append('sendMail', values.sendMail || 'yes');
  append('company_name', values.company_name ?? '');
  append('website', values.website ?? '');
  append('office', values.office ?? '');
  append('city', values.city ?? '');
  append('state', values.state ?? '');
  append('postal_code', values.postal_code ?? '');
  if (values.added_by != null && values.added_by !== '') append('added_by', String(values.added_by));
  append('address', values.address ?? '');
  append('shipping_address', values.shipping_address ?? '');
  append('note', values.note ?? '');
  if (imageFile) fd.append('image', imageFile);
  if (logoFile) fd.append('company_logo', logoFile);
  return fd;
};

const ClientForm = ({
  form,
  loading,
  submitting,
  isEdit,
  categories = [],
  subcategories = [],
  sources = [],
  employees = [],
  lead = null,
  existingImageUrl,
  existingLogoUrl,
  onSubmit,
  onCancel,
  onRefreshTaxonomy,
  excludeClientId = null,
}) => {
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [deleteImage, setDeleteImage] = useState(false);
  const [deleteLogo, setDeleteLogo] = useState(false);
  const [clientSubcategories, setClientSubcategories] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [activePhoneField, setActivePhoneField] = useState(null);

  const {
    checkPhone,
    clearDuplicate,
    duplicateInfo,
    loading: phoneCheckLoading,
    hasDuplicate,
  } = usePhoneDuplicateCheck({ excludeClientId });

  const categoryId = Form.useWatch('category_id', form);
  const mobileValue = Form.useWatch('mobile', form);
  const officeValue = Form.useWatch('office', form);
  const phonesSame =
    Boolean(digitsOnlyPhone(mobileValue)) &&
    digitsOnlyPhone(mobileValue) === digitsOnlyPhone(officeValue);
  const blockSubmit = hasDuplicate || phonesSame;

  useEffect(() => {
    setDeleteImage(false);
    setDeleteLogo(false);
    setImageFile(null);
    setLogoFile(null);
  }, [existingImageUrl, existingLogoUrl]);

  useEffect(() => {
    if (!categoryId) {
      setClientSubcategories([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingSubs(true);
      try {
        const res = await clientSubcategoriesApi.getByCategory(categoryId);
        if (!cancelled) {
          setClientSubcategories(res?.subcategories || []);
        }
      } catch {
        if (!cancelled) setClientSubcategories([]);
      } finally {
        if (!cancelled) setLoadingSubs(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [categoryId]);

  useEffect(() => {
    if (!isEdit && lead) {
      form.setFieldsValue({
        name: lead.client_name || lead.name || lead.lead_name,
        email: lead.client_email || lead.email,
        mobile: lead.mobile,
        office: lead.office || undefined,
        category_id: lead.category_id ?? undefined,
        source_id: lead.source_id ?? lead.source?.id ?? undefined,
      });
    }
  }, [lead, isEdit, form]);

  useEffect(() => {
    if (categoryId != null && categoryId !== '') {
      const sub = form.getFieldValue('sub_category_id');
      const ok = clientSubcategories.some((s) => String(s.id) === String(sub));
      if (sub != null && sub !== '' && !ok) {
        form.setFieldValue('sub_category_id', undefined);
      }
    }
  }, [categoryId, clientSubcategories, form]);

  const handleFinish = async (values) => {
    if (blockSubmit) {
      message.warning(
        phonesSame
          ? 'شماره دوم نباید با موبایل یکسان باشد'
          : 'شماره واردشده تکراری است؛ ابتدا پرونده موجود را بررسی کنید',
      );
      return;
    }
    await onSubmit(
      values,
      { imageFile, logoFile, deleteImage, deleteLogo }
    );
  };

  const showImg = !deleteImage && !imageFile && existingImageUrl;
  const showLogo = !deleteLogo && !logoFile && existingLogoUrl;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFinishFailed={() => scrollToFirstError(form)}
      className="custom-font"
      disabled={loading}
    >
      <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <div className="text-base font-semibold mb-4"><UserOutlined /> اطلاعات فردی</div>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="name" label="نام" rules={[{ required: true, message: 'نام الزامی است' }]}>
              <Input placeholder="نام مشتری" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label="ایمیل">
              <Input type="email" placeholder="email@example.com" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="mobile"
              label="موبایل"
              required
              normalize={(value) => normalizeMobileDigits(value)}
              rules={[
                { required: true, message: 'موبایل الزامی است' },
                { pattern: /^0\d{10}$/, message: 'شماره موبایل معتبر نیست' },
              ]}
              help={
                activePhoneField === 'mobile' || hasDuplicate ? (
                  <PhoneDuplicateAlert
                    info={activePhoneField === 'mobile' || !activePhoneField ? duplicateInfo : null}
                    loading={activePhoneField === 'mobile' && phoneCheckLoading}
                  />
                ) : null
              }
              validateStatus={
                (activePhoneField === 'mobile' || !activePhoneField) && hasDuplicate ? 'warning' : undefined
              }
            >
              <Input
                placeholder="09..."
                inputMode="numeric"
                maxLength={11}
                onKeyDown={(e) => {
                  const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                  if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
                  if (!/^[0-9۰-۹٠-٩]$/.test(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  setActivePhoneField('mobile');
                  checkPhone(e.target.value);
                }}
                onBlur={() => {
                  if (!digitsOnlyPhone(form.getFieldValue('mobile'))) clearDuplicate();
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="gender" label="جنسیت" initialValue="male">
              <Select placeholder="جنسیت">
                <Option value="male">مرد</Option>
                <Option value="female">زن</Option>
              </Select>
            </Form.Item>
          </Col>
          {isEdit && (
            <Col xs={24} md={12}>
              <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                <Select>
                  <Option value="active">فعال</Option>
                  <Option value="deactive">غیرفعال</Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={12}>
            <Form.Item name="login" label="ورود به پنل" initialValue="disable">
              <Select>
                <Option value="enable">فعال</Option>
                <Option value="disable">غیرفعال</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="sendMail" label="ارسال ایمیل" initialValue="yes">
              <Select>
                <Option value="yes">بله</Option>
                <Option value="no">خیر</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <div className="text-base font-semibold mb-4">دسته‌بندی</div>
        {typeof onRefreshTaxonomy === 'function' ? <ClientTaxonomyManager onRefresh={onRefreshTaxonomy} /> : null}
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="category_id" label="دسته اصلی">
              <Select allowClear placeholder="انتخاب کنید" showSearch optionFilterProp="children">
                {(categories || []).map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.category_name || c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="sub_category_id" label="زیردسته">
              <Select
                allowClear
                placeholder={categoryId ? 'انتخاب کنید' : 'ابتدا دسته اصلی را انتخاب کنید'}
                showSearch
                optionFilterProp="children"
                loading={loadingSubs}
                disabled={!categoryId}
              >
                {clientSubcategories.map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.category_name || s.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="source_id" label="شیوه آشنایی">
              <Select allowClear placeholder="انتخاب شیوه آشنایی" showSearch optionFilterProp="children">
                {(sources || []).map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.name || s.type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="added_by" label="افزوده شده توسط (کارمند)">
              <Select allowClear placeholder="انتخاب کنید" showSearch optionFilterProp="children">
                {(employees || []).map((e) => (
                  <Option key={e.id} value={e.id}>
                    {e.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <div className="text-base font-semibold mb-4"><BankOutlined /> شرکت و تماس</div>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="company_name" label="نام شرکت">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="website" label="وب‌سایت">
              <Input placeholder="https://" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="office"
              label="شماره دوم (اختیاری)"
              normalize={(value) => normalizeMobileDigits(value)}
              rules={[
                {
                  validator: async (_, value) => {
                    const officeDigits = digitsOnlyPhone(value);
                    const mobileDigits = digitsOnlyPhone(form.getFieldValue('mobile'));
                    if (officeDigits && mobileDigits && officeDigits === mobileDigits) {
                      return Promise.reject(new Error('شماره دوم نباید با موبایل یکسان باشد'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              help={
                activePhoneField === 'office' ? (
                  <PhoneDuplicateAlert info={duplicateInfo} loading={phoneCheckLoading} />
                ) : phonesSame ? (
                  <span className="text-amber-600 custom-font text-xs">
                    شماره دوم نباید با موبایل یکسان باشد
                  </span>
                ) : null
              }
              validateStatus={
                (activePhoneField === 'office' && hasDuplicate) || phonesSame ? 'warning' : undefined
              }
            >
              <Input
                placeholder="موبایل دوم یا تلفن دفتر"
                onChange={(e) => {
                  setActivePhoneField('office');
                  checkPhone(e.target.value);
                  form.validateFields(['office']).catch(() => {});
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <div className="text-base font-semibold mb-4"><EnvironmentOutlined /> آدرس</div>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item name="state" label="استان">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="city" label="شهر">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="postal_code" label="کد پستی">
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="address" label="آدرس">
              <TextArea rows={2} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="shipping_address" label="آدرس ارسال">
              <TextArea rows={2} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="note" label="یادداشت">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <div className="text-base font-semibold mb-4">تصاویر</div>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">تصویر پروفایل</div>
            {showImg ? (
              <div className="mb-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <img src={existingImageUrl} alt="تصویر پروفایل" className="h-24 rounded border object-contain bg-white" />
                {isEdit && (
                  <Button
                    danger
                    size="small"
                    className="mt-2"
                    onClick={() => { setDeleteImage(true); setImageFile(null); }}
                  >
                    حذف تصویر فعلی
                  </Button>
                )}
              </div>
            ) : null}
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setImageFile(file);
                setDeleteImage(false);
                return false;
              }}
              onRemove={() => {
                setImageFile(null);
                return true;
              }}
              fileList={imageFile ? [{ uid: '-1', name: imageFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
            </Upload>
          </Col>
          <Col xs={24} md={12}>
            <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">لوگوی شرکت</div>
            {showLogo ? (
              <div className="mb-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <img src={existingLogoUrl} alt="لوگوی شرکت" className="h-24 rounded border object-contain bg-white" />
                {isEdit && (
                  <Button
                    danger
                    size="small"
                    className="mt-2"
                    onClick={() => { setDeleteLogo(true); setLogoFile(null); }}
                  >
                    حذف لوگوی فعلی
                  </Button>
                )}
              </div>
            ) : null}
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setLogoFile(file);
                setDeleteLogo(false);
                return false;
              }}
              onRemove={() => {
                setLogoFile(null);
                return true;
              }}
              fileList={logoFile ? [{ uid: '-2', name: logoFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
            </Upload>
          </Col>
        </Row>
      </Card>

      <Space wrap>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={blockSubmit}
          className={PANEL_PRIMARY_BTN_CLASS}
        >
          ذخیره
        </Button>
        <Button onClick={onCancel}>انصراف</Button>
      </Space>
    </Form>
  );
};

export default ClientForm;
